/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { transaction } from 'mobx';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { ReviewEndpointUtterancesUIStore } from 'src/luis/appShell/buildPane/reviewEndpointPage/ReviewEndpointUtterancesUIStore';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { labelingInteractions, LabelingInteractionsAction } from 'src/luis/projects/utterances/handlers/labelingInteractions';
import {
    fromInteractionsLabelToLabelStore,
    fromLabelStoreToInteractionsLabel
} from 'src/luis/projects/utterances/handlers/labelingInteractionsUtils';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { SuggestedUtteranceObservable } from 'src/luis/projects/utterances/utils/generateSuggestedUtterancesObservables';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

type Label = Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>;

const convertLabel = (label: Label) => ({
    start: label.startTokenIndex,
    end: label.endTokenIndex,
    entityId: label.entityId.toString(),
    roleId: label.roleId?.toString()
});

export type LocalLabelingActionsInput = {
    localUIStore: ReviewEndpointUtterancesUIStore;
    selectedItems: SuggestedUtteranceObservable[];
    allItems: SuggestedUtteranceObservable[];
    getUtteranceById: (id: LuisId) => UtterancePredictionsStore;
};

export const useReviewEndpointLocalLabelingActions = (input: LocalLabelingActionsInput) => {
    const { allItems, getUtteranceById, localUIStore, selectedItems } = input;
    const { rootStore } = useGlobalState();
    const eventLoggerRef = React.useRef(getEventLogger('ReviewEndpointUtterances')).current;

    const changePredictedIntent = (utteranceId: LuisId, intentId: LuisId) => {
        const utterance = getUtteranceById(utteranceId);
        eventLoggerRef.log('ChangeSuggestedUtteranceAlignedIntent');

        utterance.utteranceStore.setLabeledIntentId(intentId);
    };

    const handleLabeling = (id: LuisId, labelingAction: LabelingInteractionsAction) =>
        transaction(() => {
            const utterance = getUtteranceById(id);

            const newLabels = labelingInteractions(
                {
                    entities: rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse.items,
                    labels: utterance.utteranceStore.labeledEntities.map(fromLabelStoreToInteractionsLabel)
                },
                labelingAction
            ).labels;

            utterance.utteranceStore.setLabels(newLabels.map(fromInteractionsLabelToLabelStore));
            localUIStore.addToDirtyUtterances([id]);
        });

    const onEntityCreated = (entityStore: EntityStore) =>
        transaction(() => {
            if (
                localUIStore.labelingStore.tokenSelectionMetadata &&
                entityStore &&
                entityStore.id &&
                isMachineLearned(entityStore.typeId)
            ) {
                handleLabeling(localUIStore.labelingStore.tokenSelectionMetadata.utteranceId, {
                    name: 'AddLabel',
                    label: convertLabel({
                        entityId: entityStore.id,
                        startTokenIndex: localUIStore.labelingStore.tokenSelectionMetadata.selection[0],
                        endTokenIndex: localUIStore.labelingStore.tokenSelectionMetadata.selection[1]
                    })
                });

                localUIStore.labelingStore.setTokenSelectionMetadata(null);
            }
            localUIStore.setIsCreateEntityModalOpen(false);
        });

    const addLabel = (id: LuisId, label: Label) => {
        eventLoggerRef.log('AddLabel');

        handleLabeling(id, {
            name: 'AddLabel',
            label: convertLabel(label)
        });
    };

    const removeLabels = (id: LuisId, labelsToRemove: Label[]) => {
        eventLoggerRef.log('RemoveLabels');

        handleLabeling(id, {
            name: 'RemoveLabels',
            labels: labelsToRemove.map(convertLabel)
        });
    };

    const confirmPredictions = (id: LuisId, labels: Label[], shouldLog = false) => {
        if (labels.length > 0) {
            if (shouldLog) {
                eventLoggerRef.log('ConfirmPredictions');
            }

            handleLabeling(id, {
                name: 'ConfirmPredictions',
                labels: labels.map(convertLabel)
            });
        }
    };

    const batchConfirmPredictions = (utteranceId?: LuisId) =>
        transaction(() => {
            const utteranceIds =
                utteranceId !== undefined
                    ? [utteranceId]
                    : selectedItems.length > 0
                    ? selectedItems.map(item => item.id)
                    : allItems.map(item => item.id);

            eventLoggerRef.log('BatchConfirmPredictions');

            const utterances = utteranceIds.map(getUtteranceById);

            utterances.forEach(({ utteranceStore, predictionStore }) => {
                confirmPredictions(utteranceStore.id, predictionStore.predictedEntities, false);
            });
        });

    return {
        changePredictedIntent,
        onEntityCreated,
        addLabel,
        removeLabels,
        confirmPredictions,
        batchConfirmPredictions
    };
};
